import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, MessageCircle } from 'lucide-react';
import { api, CONTACT } from '../lib/api';

/**
 * Cliente puro del Chat Provider corporativo (regla EMMY).
 * Sin inteligencia local: abre conversación, envía, recibe y muestra.
 * Analítica: contadores anónimos (tipo + fecha) vía /api/chat/events.
 */
export default function AnaChat() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState(() => sessionStorage.getItem('natur_ana_conv') || null);
  const endRef = useRef(null);

  const toggle = () => {
    if (!open) {
      api.logChatEvent('open').catch(() => {});
      if (!status) api.getChatStatus().then(setStatus).catch(() => setStatus({ available: false, offlineMessage: 'ANA estará disponible próximamente.\nMientras tanto puedes contactar directamente con nosotros por WhatsApp.' }));
    }
    setOpen((v) => !v);
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending || !status?.available) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setSending(true);
    try {
      let cid = convId;
      if (!cid) {
        const conv = await api.openChatConversation();
        cid = conv.conversationId;
        setConvId(cid);
        sessionStorage.setItem('natur_ana_conv', cid);
        api.logChatEvent('conversation_started').catch(() => {});
      }
      const res = await api.sendChatMessage(cid, text);
      const replies = (res.messages || []).filter((m) => m.text).map((m) => ({ role: 'assistant', text: m.text }));
      (res.events || []).forEach((ev) => api.logChatEvent(ev).catch(() => {}));
      setMessages((m) => [...m, ...replies]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: 'No he podido responder en este momento. Inténtalo de nuevo en unos instantes.' }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Burbuja ANA (encima de WhatsApp): estrellas · nombre · imagen */}
      <button
        onClick={toggle}
        className="fixed bottom-[5.5rem] right-5 z-50 bg-stone-900 hover:bg-stone-700 transition-colors text-white h-14 pl-4 pr-1.5 rounded-full flex items-center gap-2 shadow-xl"
        data-testid="ana-chat-bubble"
        aria-label="Abrir chat con ANA"
      >
        <Sparkles className="w-5 h-5 text-amber-400" />
        <span className="font-black text-sm tracking-wide">ANA</span>
        <img src="/ana-avatar.png" alt="ANA" className="w-11 h-11 rounded-full object-cover border-2 border-amber-400 bg-white" data-testid="ana-bubble-avatar" />
      </button>

      {/* Ventana de conversación */}
      {open && (
        <div className="fixed bottom-[9.5rem] right-5 z-50 w-[92vw] max-w-sm bg-white rounded-xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden" style={{ height: '460px' }} data-testid="ana-chat-window">
          <div className="bg-stone-900 text-white px-4 py-3 flex items-center gap-3">
            <img src="/ana-avatar.png" alt="ANA" className="w-10 h-10 rounded-full object-cover border-2 border-amber-400 bg-white shrink-0" data-testid="ana-header-avatar" />
            <div className="flex-1">
              <p className="font-black text-sm leading-tight flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-amber-400" /> ANA</p>
              <p className="text-[11px] text-stone-400">Asistente de Natur Autoescuela</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-white transition-colors" data-testid="ana-chat-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {status && !status.available ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-4" data-testid="ana-offline-message">
              <img src="/ana-avatar.png" alt="ANA" className="w-20 h-20 rounded-full object-cover border-2 border-amber-400 bg-stone-100" data-testid="ana-offline-avatar" />
              <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-line">{status.offlineMessage}</p>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="bg-green-500 hover:bg-green-600 transition-colors text-white font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-2"
                data-testid="ana-offline-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> Abrir WhatsApp
              </a>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-stone-50" data-testid="ana-chat-messages">
                {messages.length === 0 && (
                  <p className="text-xs text-stone-400 text-center pt-6">Escribe tu consulta y ANA te responderá al momento.</p>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'ml-auto bg-amber-400 text-stone-900' : 'bg-white border border-stone-200 text-stone-700'}`}>
                    {m.text}
                  </div>
                ))}
                {sending && <p className="text-xs text-stone-400">ANA está escribiendo…</p>}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="border-t border-stone-200 p-3 flex items-center gap-2 bg-white">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escribe tu mensaje…"
                  className="flex-1 border border-stone-300 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="ana-chat-input"
                />
                <button type="submit" disabled={sending || !input.trim()} className="btn-natur w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40" data-testid="ana-chat-send">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
